import { v4 as uuid } from 'uuid';
import find from 'lodash/find';
import RxStructureService from '@Services/RxStructureService';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import HeatmapService from '@Pages/Heatmap/HeatmapService';
import Breadcrumb from '@Services/Breadcrumb';
import RXService from '@Services/RxService';
import { setCurrentSettingsToAppStorage } from '@Pages/Heatmap/HeatmapSettingsStorage';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import RelativeContextService from '@Containers/RelativeContext/RelativeContextNgService';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';
import { generateHeatmapDataImprint } from '@Pages/Heatmap/HeatmapDataImprint';
import { openDialog as openCellDetailsDialog } from '@Pages/Heatmap/HeatmapCellDetailsDialog/actions';
import { updateDialog as updateDialogAction, closeDialog as closeDialogAction } from '@Containers/Dialogs/DialogsActions';
import { dialog as dialogSelector } from '@Containers/Dialogs/DialogsSelectors';
import { typeOfHeatmapLegendDialog } from '@Pages/Heatmap/HeatmapLegendDialog/constants';
import { typeOfHeatmapNavigatorDialog } from '@Pages/Heatmap/HeatmapNavigatorDialog/constants';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import {
  SET_HEATMAP_OPEN,
  SET_SETTINGS,
  FETCH_HEATMAP_DATA,
  FETCH_HEATMAP_DATA_SUCCESS,
  FETCH_HEATMAP_DATA_FAILURE,
  UPDATE_HEATMAP_DATA,
  SET_SELECTION_INFO,
  HEATMAP_OBSERVABLE_TOPICS,
} from './constants';
import {
  settings as settingsSelector,
  heatmapData as heatmapDataSelector,
  selectionInfoMappedForRequest as selectionInfoMappedForRequestSelector,
} from './selectors';

/* Old actions */
export function openHeatmap() {
  return {
    type: SET_HEATMAP_OPEN,
    payload: true,
  };
}

export function closeHeatmap() {
  return {
    type: SET_HEATMAP_OPEN,
    payload: false,
  };
}

export function setSettings(settings) {
  return (dispatch) => {
    dispatch({
      type: SET_SETTINGS,
      payload: settings,
    });
    setCurrentSettingsToAppStorage(settings);
  };
}

/* Toolbar actions */
export function toggleFullScreenMode(isFullScreen) {
  return (dispatch, getState) => {
    const settings = settingsSelector(getState());
    dispatch(
      setSettings({
        ...settings,
        startInFullScreen: isFullScreen,
      })
    );
  };
}

function showNavigator() {
  return (dispatch, getState) => {
    const { matrixName } = heatmapDataSelector(getState());
    dispatch(
      updateDialogAction({
        id: typeOfHeatmapNavigatorDialog,
        dialogType: typeOfHeatmapNavigatorDialog,
        backdrop: false,
        animated: false,
        matrixName,
      })
    );
  };
}

function hideNavigator() {
  return (dispatch) => {
    dispatch(
      closeDialogAction({
        id: typeOfHeatmapNavigatorDialog,
      })
    );
  };
}

export function toggleNavigator() {
  return (dispatch, getState) => {
    const isNavigatorDialogOpened = dialogSelector(getState(), { id: typeOfHeatmapNavigatorDialog });
    if (isNavigatorDialogOpened) {
      dispatch(hideNavigator());
    } else {
      dispatch(showNavigator());
    }
  };
}

function showLegend() {
  return (dispatch) => {
    dispatch(
      updateDialogAction({
        id: typeOfHeatmapLegendDialog,
        dialogType: typeOfHeatmapLegendDialog,
        backdrop: false,
      })
    );
  };
}

function hideLegend() {
  return (dispatch) => {
    dispatch(
      closeDialogAction({
        id: typeOfHeatmapLegendDialog,
      })
    );
  };
}

export function toggleLegend() {
  return (dispatch, getState) => {
    const isLegendDialogOpened = dialogSelector(getState(), { id: typeOfHeatmapLegendDialog });
    if (isLegendDialogOpened) {
      dispatch(hideLegend());
    } else {
      dispatch(showLegend());
    }
  };
}

function updateHeatmapModeInBreadCrumb(params, isHeatmapShown) {
  const breadcrumb = {
    ...params,
    isHeatmapShown,
  };

  Breadcrumb.updateBreadcrumbs(breadcrumb);
}

export function showResultsList({ fromContext, searchId, limitOrExclude }, relativeContexts) {
  return (dispatch, getState) => {
    const contextParams = relativeContexts.find((item) => item.get('context') === fromContext);
    const nextParams = {
      ...(contextParams && contextParams.toJS()),
      searchId,
      limitOrExclude,
    };

    updateHeatmapModeInBreadCrumb(nextParams, false);

    const params = getCombinedRouteParams(getState());
    const combinedParams = { ...params, ...nextParams };
    combinedParams.fromContext = null;
    const pathName = generateResultsPath(combinedParams);
    dispatch(closeHeatmap());
    history.push({
      pathname: pathName,
      state: combinedParams,
    });
  };
}

const limitToHeatmapResults = withGlobalSpinnerActionEnhancer((options, { hitSetKey, fromContext }) => async (dispatch, getState) => {
  const heatmapData = heatmapDataSelector(getState());

  try {
    const payload = {
      matrixName: heatmapData.matrixName,
      hitSetKey,
      ...options,
    };
    const limitResponse = await HeatmapService.sendExcludeRequest(payload);

    // limitTo/exclude return 'dpitems' context
    // so we need to switch back to current context and then create breadcrumb to keep context between breadcrumbs

    const relativeContexts = await RelativeContextService.getRelativeContexts({
      resultName: limitResponse.resultName,
      databaseId: limitResponse.databaseId,
      context: limitResponse.context,
      hitSetKey: limitResponse.hitSetKey,
      resultSize: limitResponse.resultSize,
    });

    const relativeContext = find(relativeContexts, ({ context }) => context === fromContext);

    if (!relativeContext) {
      return;
    }

    const searchId = uuid();
    const breadcrumb = {
      searchId,
      isHeatmapShown: true,
      resultName: relativeContext.resultName,
      databaseId: relativeContext.databaseId,
      context: relativeContext.context,
      hitSetKey: relativeContext.hitSetKey,
      heatmapSettings: relativeContext.heatmapSettings,
      heatmapResultName: limitResponse.resultName,
      sortedMatrixName: limitResponse.sortedMatrixName,
    };

    await Breadcrumb.createBreadcrumb(breadcrumb, relativeContext.resultSize);

    const params = getCombinedRouteParams(getState());
    const combinedParams = {
      ...params,
      ...limitResponse,
      sortedMatrixName: null,
      origSortedMatrixName: null,
      resetBreadcrumbs: false,
    };
    const pathName = generateHeatmapPath(combinedParams);
    history.push({
      pathname: pathName,
      state: combinedParams,
    });
  } catch (error) {
    Logger.error(error);
  }
});

export function limitTo(options, params) {
  return (dispatch, getState) => {
    const selectionInfoMapped = selectionInfoMappedForRequestSelector(getState());
    return dispatch(
      limitToHeatmapResults(
        {
          ...selectionInfoMapped,
          ...options,
        },
        params
      )
    );
  };
}

/* Cell click actions */

function onCellClick(params, cellData) {
  return (dispatch, getState) => {
    const { matrixName, headers } = heatmapDataSelector(getState());
    const settings = settingsSelector(getState());
    const axisDescription = {
      x: {
        title: settings.col.title,
        label: headers.cols[cellData.column].label,
        subUnits: headers.cols[cellData.column].subunits,
      },
      y: {
        title: settings.row.title,
        label: headers.rows[cellData.row].label,
        subUnits: headers.rows[cellData.row].subunits,
      },
    };

    dispatch(openCellDetailsDialog({ matrixName, params, cellData, axisDescription }));
  };
}

/* Data fetch actions */
let unObserveDataReceived;
export const fetchHeatmapData = withGlobalSpinnerActionEnhancer((params, activeBreadcrumb) => async (dispatch, getState) => {
  const { resultName } = params;
  const settings = settingsSelector(getState());

  // Update active breadcrumb
  if (activeBreadcrumb.heatmapResultName !== resultName) {
    const breadcrumb = {
      ...params,
      isHeatmapShown: true,
      heatmapResultName: resultName,
    };

    delete breadcrumb.context;
    Breadcrumb.updateBreadcrumbs(breadcrumb);
  }

  if (unObserveDataReceived) {
    unObserveDataReceived();
  }

  unObserveDataReceived = HeatmapService.init();

  dispatch({
    type: FETCH_HEATMAP_DATA,
    payload: generateHeatmapDataImprint(params, settings),
  });

  try {
    const data = await HeatmapService.createMatrix(params);
    dispatch({
      type: FETCH_HEATMAP_DATA_SUCCESS,
      payload: data,
    });
    dispatch(showNavigator());
  } catch (error) {
    Logger.error(error);
    dispatch({
      type: FETCH_HEATMAP_DATA_FAILURE,
      payload: error,
    });
  }
});

export function onMatrixReceived(params) {
  return (dispatch) => {
    RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.clickCell, (...args) => {
      dispatch(onCellClick(params, ...args));
    });

    HeatmapService.registerWrapperCallback(() => {
      const data = HeatmapService.getHeatmapData();
      dispatch({
        type: UPDATE_HEATMAP_DATA,
        payload: { ...data },
      });
    });
  };
}

/* Heatmap component actions */
export function onInitData(rowsToShow, colsToShow) {
  return () => {
    HeatmapService.onInitData(rowsToShow, colsToShow);
  };
}

export function onRequestData(newRowStart, newCellStart, rowsToShow, colsToShow, isDragging) {
  return () => {
    HeatmapService.onRequestData(newRowStart, newCellStart, rowsToShow, colsToShow, isDragging);
  };
}

export function onSelectionChange(selectionInfo) {
  return (dispatch) => {
    dispatch({
      type: SET_SELECTION_INFO,
      payload: selectionInfo,
    });
  };
}

export function copyStructureToClipboard(structure) {
  return () => {
    navigator.clipboard.writeText(structure);
    showCopiedToastMessage();
  };
}

export function copyStructureToQuery({ structure, preview }) {
  return () => {
    RxStructureService.copyToQuery({ structure, preview });
  };
}

export const showResultsContextForSelection = withGlobalSpinnerActionEnhancer(() => async (dispatch, getState) => {
  const { matrixName } = heatmapDataSelector(getState());
  const selectionInfoMapped = selectionInfoMappedForRequestSelector(getState());
  try {
    const response = await MessageBroker.send(RequestUrls.heatmapDetails, {
      ...selectionInfoMapped,
      matrixName,
    });
    const searchParams = {
      ...response,
      searchId: uuid(),
      resetBreadcrumbs: false,
    };
    await Breadcrumb.createBreadcrumb(searchParams, response.resultSize);
    const params = getCombinedRouteParams(getState());
    const combinedParams = { ...params, ...searchParams };
    const pathName = generateResultsPath(combinedParams);
    history.push({
      pathname: pathName,
      state: combinedParams,
    });
  } catch (e) {
    Logger.error(e);
  }
});

export const onUnmount = () => (dispatch) => {
  dispatch(hideLegend());
  dispatch(hideNavigator());
};

export const selectDatabase =
  ({ context, databaseId, resultName, searchId, sort, order, hitSetKey, isFromAdvancedSearch }) =>
  () => {
    const params = { context, databaseId, resultName, hitSetKey, searchId, pageNumber: 1, order, sort, isFromAdvancedSearch };
    const pathName = generateResultsPath(params);
    history.push({
      pathname: pathName,
      state: params,
    });
  };
